/**
 * Formats a date string in 'YYYY', 'YYYY-MM', or 'YYYY-MM-DD' format
 * into a human-readable string.
 * Examples:
 *   '2024-06-01' => 'June 1, 2024'
 *   '2024-06'    => 'June 2024'
 *   '2024'       => '2024'
 *   ''           => ''
 *
 * @param input - The date string to format.
 * @returns The formatted date string.
 */
export function formatDate(input: string): string {
	if (!input || typeof input !== 'string') return '';

	const parts = input.split('-').filter(Boolean);

	if (parts.length === 0) return '';

	const [year, month, day] = parts;

	// Validate year
	if (!/^\d{4}$/.test(year)) return '';

	// Month names array
	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	// Only year provided
	if (parts.length === 1) {
		return year;
	}

	// Year and month provided
	if (parts.length === 2) {
		const monthIndex = Number(month) - 1;
		if (monthIndex < 0 || monthIndex > 11 || isNaN(monthIndex)) return year;
		return `${monthNames[monthIndex]} ${year}`;
	}

	// Year, month, and day provided
	if (parts.length === 3) {
		const monthIndex = Number(month) - 1;
		const dayNum = Number(day);
		if (
			monthIndex < 0 ||
			monthIndex > 11 ||
			isNaN(monthIndex) ||
			dayNum < 1 ||
			dayNum > 31 ||
			isNaN(dayNum)
		) {
			return year;
		}
		return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
	}

	// Fallback: return empty string for unexpected input
	return '';
}

/**
 * Formats a given date and appends a calculated age or elapsed duration.
 *
 * - For type "dob": returns "Month D, YYYY (N years old)".
 * - For type "doi": returns "Month D, YYYY (X years and Y months|X months and Y days|X days ago)".
 * - If the date is in the future, appends "(Date is in the future)".
 * - Returns an empty string if any of d, m, or y is missing.
 *
 * @param dateObj Object with day, month, and year as strings (e.g., { d: "01", m: "January", y: "2000" }).
 * @param type Determines the calculation mode: "dob" (age) or "doi" (elapsed duration).
 * @returns A formatted date string with age or elapsed time information.
 */
export function formatDateAndCalculateAge(
	dateObj: { d: string; m: string; y: string },
	type: 'dob' | 'doi'
): string {
	if (!dateObj?.d || !dateObj?.m || !dateObj?.y) return '';

	const dateString = `${dateObj.m} ${dateObj.d}, ${dateObj.y}`;
	const targetDate = new Date(dateString);
	const today = new Date();

	const formattedDate = targetDate.toLocaleDateString('en-US', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});

	const diffInMs = today.getTime() - targetDate.getTime();

	if (diffInMs < 0) {
		return `${formattedDate} (Date is in the future)`;
	}

	if (type === 'dob') {
		let age = today.getFullYear() - targetDate.getFullYear();
		const m = today.getMonth() - targetDate.getMonth();

		if (m < 0 || (m === 0 && today.getDate() < targetDate.getDate())) {
			age--;
		}

		return `${formattedDate} (${age} years old)`;
	} else {
		let years = today.getFullYear() - targetDate.getFullYear();
		let months = today.getMonth() - targetDate.getMonth();
		let days = today.getDate() - targetDate.getDate();

		if (days < 0) {
			months--;
			days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
		}

		if (months < 0) {
			years--;
			months += 12;
		}

		let durationString = '';
		if (years > 0) {
			durationString = `${years} ${years === 1 ? 'year' : 'years'} ${
				months > 0 ? `and ${months} months` : ''
			}`;
		} else if (months > 0) {
			durationString = `${months} ${months === 1 ? 'month' : 'months'} ${
				days > 0 ? `and ${days} days` : ''
			}`;
		} else {
			durationString = `${days} ${days === 1 ? 'day' : 'days'}`;
		}

		if (durationString.trim() === '') {
			durationString = 'less than a day';
		}

		return `${formattedDate} (${durationString} ago)`;
	}
}

export const months = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

/**
 * Checks whether the provided day, month (by name), and year form a valid calendar date
 * and compares it against today (00:00). Returns true if the date is strictly in the future.
 *
 * - Rejects missing or non-numeric day/year and unknown month names.
 * - Validates that the constructed date matches the given components.
 *
 * @param day - Day of month as a string (e.g., "15").
 * @param month - Month name corresponding to an entry in `months` (e.g., "March").
 * @param year - Four-digit year as a string (e.g., "2025").
 * @returns True if the date is valid and after today; otherwise, false.
 */ export function isValidDateNotInFuture(
	day: string,
	month: string,
	year: string
): boolean {
	if (!day || !month || !year) return false;

	const dayNum = parseInt(day);
	const yearNum = parseInt(year);
	const monthNum = months.indexOf(month) + 1;

	// Fixed: Added return statements
	if (isNaN(dayNum) || isNaN(yearNum) || monthNum === 0) return false;

	const date = new Date(yearNum, monthNum - 1, dayNum);
	const isValidDate =
		date.getDate() === dayNum &&
		date.getMonth() === monthNum - 1 &&
		date.getFullYear() === yearNum;

	if (!isValidDate) return false;

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	return date > today;
}
